import React, { useEffect, useRef, useState } from 'react';
import { formatClientApiError, isProcessingCancelled } from '../api';

/**
 * ProcessingPanel - runs the API call on mount.
 * When fileProgress.total is set (single or bulk async job), shows live server stage.
 * Otherwise falls back to a short animated step list.
 */

const FALLBACK_STEPS = [
  'Reading document...',
  'Extracting financial fields...',
  'Building processed PDF...',
  'Saving to Zoho WorkDrive and Sheet...',
];

const STEP_INTERVAL_MS = 700;

const styles = {
  container: { padding: '32px 0' },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: 24,
  },
  liveStage: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text-heading)',
    marginBottom: 8,
    lineHeight: 1.35,
  },
  liveFilename: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginBottom: 20,
    wordBreak: 'break-word',
  },
  liveHint: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'background 0.2s, box-shadow 0.2s',
  },
  stepText: {
    fontSize: 14,
    transition: 'color 0.2s',
  },
  barTrack: {
    height: 2,
    background: 'var(--border)',
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 28,
  },
  barFill: {
    height: '100%',
    background: 'var(--blue)',
    borderRadius: 2,
    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  progressFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginTop: 6,
    gap: 12,
  },
  percent: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
  },
  eta: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-secondary)',
    textAlign: 'right',
    flex: 1,
  },
  cancelSection: {
    marginTop: 32,
    paddingTop: 24,
    borderTop: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  cancelBtn: {
    padding: '10px 18px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  cancelNote: {
    margin: 0,
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
    lineHeight: 1.5,
    maxWidth: 420,
  },
};

function formatEtaLabel(
  elapsedSeconds,
  estimatedSeconds,
  { apiDone, finishing, bulkInFlight, currentStage },
) {
  if (finishing) return 'Wrapping up...';
  if (apiDone) return 'Almost done...';
  if (bulkInFlight && elapsedSeconds > estimatedSeconds) {
    return currentStage ? `Still working - ${currentStage}` : 'Still working on this file…';
  }
  if (bulkInFlight) {
    return elapsedSeconds > estimatedSeconds
      ? 'Still working on this file…'
      : `~${Math.max(1, Math.ceil((estimatedSeconds - elapsedSeconds) / 60))} min remaining`;
  }

  const remaining = estimatedSeconds - elapsedSeconds;
  if (remaining > 60) {
    return `~${Math.ceil(remaining / 60)} min remaining`;
  }
  if (remaining > 0) {
    return 'Less than 1 min remaining';
  }
  return 'Almost done...';
}

async function formatApiError(err) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const t = await data.text();
      const j = JSON.parse(t);
      if (j.detail) {
        return typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
      }
    } catch {
      if (err.response?.status === 413) return 'File too large.';
    }
  } else if (data?.detail) {
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  return formatClientApiError(err);
}

function formatBulkProgressLabel(fileProgress) {
  if (!fileProgress || !fileProgress.total) return null;
  const { processed, total, currentFilename } = fileProgress;
  if (processed >= total) {
    return `Finishing (${total} file${total === 1 ? '' : 's'})…`;
  }
  const current = Math.min(processed + 1, total);
  const base = `File ${current} of ${total}`;
  if (currentFilename) {
    const short = currentFilename.length > 48
      ? `${currentFilename.slice(0, 45)}…`
      : currentFilename;
    return `${base}: ${short}`;
  }
  return base;
}

function computeLiveProgress(fileProgress) {
  const { processed, total, currentFilename } = fileProgress;
  if (processed >= total) return 100;
  const inFlight = currentFilename ? 0.35 : 0.1;
  return Math.min(99, Math.round(((processed + inFlight) / total) * 100));
}

export default function ProcessingPanel({
  apiCall,
  onDone,
  onCancel,
  estimatedSeconds = 90,
  fileProgress = null,
}) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [apiResult, setApiResult] = useState(null);
  const [finished, setFinished] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef(null);
  const onDoneCalledRef = useRef(false);
  const inflightRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const userCancelledRef = useRef(false);

  const isLiveJob = Boolean(fileProgress?.total);

  useEffect(() => {
    let cancelled = false;

    const subscribe = (promise) => {
      promise
        .then((result) => {
          if (cancelled || userCancelledRef.current) return;
          setApiResult({ data: result, error: null });
        })
        .catch(async (err) => {
          if (cancelled || userCancelledRef.current || isProcessingCancelled(err)) return;
          const message = await formatApiError(err);
          const partialData = err?.manifest?.items?.length
            ? { manifest: err.manifest, jobId: err.jobId || null, partial: Boolean(err.partial) }
            : null;
          setApiResult({ data: partialData, error: message });
        })
        .finally(() => {
          if (inflightRef.current === promise) {
            inflightRef.current = null;
          }
        });
    };

    if (inflightRef.current) {
      subscribe(inflightRef.current);
      return () => { cancelled = true; };
    }

    const promise = apiCall();
    inflightRef.current = promise;
    subscribe(promise);

    return () => { cancelled = true; };
  }, [apiCall]);

  const handleCancel = () => {
    if (userCancelledRef.current) return;
    userCancelledRef.current = true;
    clearInterval(intervalRef.current);
    onDoneCalledRef.current = true;
    onCancel?.();
  };

  useEffect(() => {
    if (isLiveJob) return undefined;
    intervalRef.current = setInterval(() => {
      setVisibleCount((prev) => Math.min(prev + 1, FALLBACK_STEPS.length));
    }, STEP_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [isLiveJob]);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setElapsedSeconds(0);
    const tick = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (
      apiResult
      && (isLiveJob || visibleCount >= Math.floor(FALLBACK_STEPS.length / 2))
      && !finished
      && !onDoneCalledRef.current
      && !userCancelledRef.current
    ) {
      onDoneCalledRef.current = true;
      clearInterval(intervalRef.current);
      if (!isLiveJob) setVisibleCount(FALLBACK_STEPS.length);
      setFinished(true);
      setTimeout(() => onDone(apiResult.data, apiResult.error), 500);
    }
  }, [apiResult, visibleCount, finished, onDone, isLiveJob]);

  const liveProgress = isLiveJob ? computeLiveProgress(fileProgress) : null;
  const fallbackProgress = Math.min(100, Math.round((visibleCount / FALLBACK_STEPS.length) * 100));
  const progress = isLiveJob ? liveProgress : fallbackProgress;

  const bulkLabel = formatBulkProgressLabel(fileProgress);
  const currentStage = fileProgress?.currentStage;
  const bulkInFlight = Boolean(
    isLiveJob
    && fileProgress.processed < fileProgress.total
    && (fileProgress.currentFilename || currentStage),
  );

  const etaLabel = formatEtaLabel(elapsedSeconds, estimatedSeconds, {
    apiDone: Boolean(apiResult) && !finished,
    finishing: finished,
    bulkInFlight,
    currentStage,
  });

  return (
    <div style={styles.container}>
      <div style={styles.label}>
        {bulkLabel || 'processing document'}
      </div>

      {isLiveJob ? (
        <>
          <div style={styles.liveStage} aria-live="polite">
            {currentStage || 'Working…'}
          </div>
          {fileProgress.currentFilename ? (
            <div style={styles.liveFilename}>
              {fileProgress.currentFilename}
            </div>
          ) : null}
          <p style={styles.liveHint}>
            Status updates every few seconds from the server. Large emails can take several minutes.
          </p>
        </>
      ) : (
        FALLBACK_STEPS.map((step, i) => {
          const isVisible = i < visibleCount;
          const isDoneStep = isVisible && i < visibleCount - 1;
          const isCurrent = i === visibleCount - 1;

          return (
            <div
              key={i}
              style={{
                ...styles.stepRow,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            >
              <div style={{
                ...styles.dot,
                background: isDoneStep || (finished && isVisible)
                  ? 'var(--accent)'
                  : isCurrent
                    ? 'var(--brand)'
                    : 'var(--border)',
                boxShadow: isCurrent && !finished ? '0 0 6px color-mix(in srgb, var(--brand) 40%, var(--white))' : 'none',
              }} />
              <span style={{
                ...styles.stepText,
                color: isDoneStep
                  ? 'var(--text-secondary)'
                  : isCurrent
                    ? 'var(--text-heading)'
                    : 'var(--text-tertiary)',
              }}>
                {step}
              </span>
            </div>
          );
        })
      )}

      <div style={styles.barTrack}>
        <div style={{ ...styles.barFill, width: `${progress}%` }} />
      </div>
      <div style={styles.progressFooter}>
        <span style={styles.percent}>{progress}%</span>
        <span style={styles.eta} aria-live="polite">
          {etaLabel}
        </span>
      </div>

      <div style={styles.cancelSection}>
        <button
          type="button"
          style={styles.cancelBtn}
          onClick={handleCancel}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-active)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }}
        >
          Cancel processing
        </button>
        <p style={styles.cancelNote}>
          If you cancel, no progress will be saved.
        </p>
      </div>
    </div>
  );
}
